/**
 * Add Avatar Button Renderer
 *
 * Avatar upload button with circular preview and remove functionality
 */

import React from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import * as LucideIcons from 'lucide-react';
import type { FieldRenderer } from '@/MyCode/core';
import type { FormField } from '@/MyCode/components/JsonFormSteps';
import { useFileUpload } from '@/MyCode/hooks/use-file-upload';

/**
 * Separate component for AddAvatarButton to properly handle hooks
 */
const AddAvatarButtonComponent: React.FC<{
    field: FormField;
    value: any;
    handleChange: (name: string, value: any) => void;
    isRequired: boolean;
}> = ({ field, value, handleChange, isRequired }) => {
    const [{ files }, { removeFile, openFileDialog, getInputProps }] = useFileUpload({
        accept: 'image/*',
    });

    // Use ref to track the previous preview URL to avoid infinite loops
    const prevPreviewRef = React.useRef<string>('');

    // Sync files with form value only when files change
    React.useEffect(() => {
        if (files.length > 0) {
            const newPreview = files[0].preview;
            // Only update if the preview has actually changed
            if (prevPreviewRef.current !== newPreview) {
                prevPreviewRef.current = newPreview;
                handleChange(field.name, newPreview);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [files]);

    const previewUrl = files[0]?.preview || value || null;
    const fileName = files[0]?.file.name || null;

    const CircleUserRoundIcon = LucideIcons.CircleUserRound;
    const XIcon = LucideIcons.X;

    const handleRemove = React.useCallback(() => {
        if (files[0]?.id) {
            removeFile(files[0].id);
        }
        prevPreviewRef.current = '';
        handleChange(field.name, '');
    }, [files, removeFile, handleChange, field.name]);

    return (
        <div className="space-y-2">
            <Label>
                {field.label}
                {isRequired && <span className="text-destructive ml-1">*</span>}
            </Label>
            <div className="flex flex-col items-center gap-2">
                <div className="relative inline-flex">
                    <Button
                        type="button"
                        variant="outline"
                        className="relative size-16 overflow-hidden rounded-full p-0 shadow-none"
                        onClick={openFileDialog}
                        aria-label={previewUrl ? 'Change image' : 'Upload image'}
                    >
                        {previewUrl ? (
                            <img
                                className="size-full object-cover"
                                src={previewUrl}
                                alt="Preview of uploaded image"
                                width={64}
                                height={64}
                                style={{ objectFit: 'cover' }}
                            />
                        ) : (
                            <div aria-hidden="true">
                                <CircleUserRoundIcon className="size-4 opacity-60" />
                            </div>
                        )}
                    </Button>
                    {previewUrl && (
                        <Button
                            type="button"
                            onClick={handleRemove}
                            size="icon"
                            className="absolute -top-2 -right-2 size-6 rounded-full border-2 border-background shadow-none focus-visible:border-background"
                            aria-label="Remove image"
                        >
                            <XIcon className="size-3.5" />
                        </Button>
                    )}
                    <input
                        {...getInputProps()}
                        className="sr-only"
                        aria-label="Upload image file"
                        tabIndex={-1}
                    />
                </div>
                {fileName && <p className="text-xs text-muted-foreground">{fileName}</p>}
                {field.placeholder && (
                    <p
                        aria-live="polite"
                        role="region"
                        className="text-xs text-muted-foreground text-center"
                    >
                        {field.placeholder}
                    </p>
                )}
            </div>
        </div>
    );
};

/**
 * Avatar Upload Button Renderer
 * Features:
 * - Circular avatar preview
 * - File upload with drag & drop support
 * - Remove uploaded image
 * - Custom placeholder text
 * - Default avatar icon
 */
export const renderAddAvatarButton: FieldRenderer = ({ field, value, handleChange, isRequired }) => {
    return (
        <AddAvatarButtonComponent
            field={field}
            value={value}
            handleChange={handleChange}
            isRequired={isRequired}
        />
    );
};

export default renderAddAvatarButton;
